import {Component} from '@wonderlandengine/api';
import {getCookie, setCookie} from './utils/cookies.js';
import profile from './utils/profile.js';
import {user} from 'uber-sdk/user.js';

const CSS = `
        /* Style for the modal container */
        .modal-container {
            font-family: sans-serif;
            display: none;
            justify-content: center;
            align-items: center;
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background-color: rgba(0, 0, 0, 0.5);
            z-index: 1000;
        }

        /* Style for the modal content */
        .modal-content {
            background-color: #fff;
            border-radius: 15px;
            padding: 20px;
            box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.5);
            text-align: center;
            margin: 5px;
            max-height: 100vh;
        }

        /* Style for the input field */
        .username-input {
            border: none;
            border-radius: 25px;
            padding: 10px;
            font-size: 16px;
        }

        /* Style for the submit button */
        .submit-button {
            background-color: #007BFF;
            color: #fff;
            border: none;
            border-radius: 25px;
            margin-top: 10px;
            padding: 10px 20px;
            font-size: 16px;
            cursor: pointer;
        }

        .privacy-text {
            height: auto;
            max-height: 55vh;
            overflow: scroll;
            text-align: left;
            border: 1px grey;
        }
`;
const NAME_INPUT_HTML = `
        <!-- Modal content -->
        <div class="modal-content">
            <h2>Enter Your Name</h2>
            <input type="text" id="username-input" class="username-input" placeholder="Username" onkeydown="event.stopPropagation()">
            <button id="submit-button" class="submit-button">Play!</button>
        </div>
`;

const lastPrivacyChange = 1715346300000;
const PRIVACY_HTML = `
        <!-- Modal content -->
        <div class="modal-content">
            <h2>Privacy Policy</h2>
            <p>We take your privacy seriously. Here's what you need to know:</p>

            <div class="privacy-text" >
                <h3>1. Information We Collect</h3>
                <ul>
                    <li>While playing "WebXR Paintball," we collect basic gameplay data for analytics purposes via GameAnalytics and HeyVR.</li>
                    <li>Our multiplayer service collects and temporarily stores data only while you're connected.</li>
                </ul>
                <h3>2. How We Use Your Information</h3>
                <p>We use collected data to improve game performance, user experience, and provide multiplayer functionality.</p>
                <h3>3. Third-Party Services</h3>
                <p>We utilize GameAnalytics for analytics and HeyVR for your user data, leaderboards, and digital purchases.
                   Please review their privacy policies for more information.</p>
                <h3>4. Data Storage</h3>
                <p>Data stored on our servers is limited to the duration of your multiplayer session and is purged once you disconnect.</p>
                <h3>5. Your Rights</h3>
                <p>You have the right to access, correct, or delete your personal information. Contact us for assistance.</p>
                <h3>6. Updates</h3>
                <p>This Privacy Policy may be updated. Check back periodically for any changes.</p>
                <h3>7. Contact Us</h3>
                <p>For any privacy concerns or inquiries, reach out to us at <a href="mailto:privacy@wonderlandengine.com">privacy@wonderlandengine.com</a>.</p>
                <p>By playing "WebXR Paintball," you agree to this Privacy Policy.</p>
                <p>Last updated: May 10th 2024</p>
            </div>

            <button id="accept-button" class="submit-button">Accept</button>
        </div>
`;

/**
 * name-modal
 */
export class NameModal extends Component {
    static TypeName = 'name-modal';

    modal: HTMLDivElement | null = null;
    privacyModal: HTMLDivElement | null = null;
    username: string = '';

    lastAcceptedPrivacy = -1;

    init() {
        const style = document.createElement('style');
        style.innerText = CSS;
        document.head.append(style);

        this.modal = document.createElement('div');
        this.modal.classList.add('modal-container');
        this.modal.id = 'modal-container';
        document.body.appendChild(this.modal);
        this.modal.innerHTML = NAME_INPUT_HTML;

        const privacyModal = document.createElement('div');
        privacyModal.classList.add('modal-container');
        privacyModal.id = 'privacy-container';
        privacyModal.innerHTML = PRIVACY_HTML;
        this.privacyModal = privacyModal;
        document.body.appendChild(privacyModal);

        try {
            const timestampStr = getCookie('paintball-privacy');
            if (timestampStr) this.lastAcceptedPrivacy = Number.parseInt(timestampStr);
        } catch (e) {
            /* Probably just not set yet */
        }
    }

    onDestroy() {
        if (this.modal && this.modal.parentNode)
            this.modal.parentNode.removeChild(this.modal);
        if (this.privacyModal && this.privacyModal.parentNode)
            this.privacyModal.parentNode.removeChild(this.privacyModal);
    }

    async start() {
        if (this.lastAcceptedPrivacy < lastPrivacyChange) {
            this.openPrivacyModal();
        }

        if (!user.hasProviders()) {
            this.username = profile.getUsername();
            if (this.username.startsWith('Guest')) {
                this.openUsernameModal();
            }
        } else {
            const u = await user.requestLogin();
            this.username = u.name ?? 'Guest';
        }
    }
    openPrivacyModal() {
        if (!this.privacyModal) return;
        this.privacyModal.style = 'display: flex';

        const btn = document.getElementById('accept-button');
        if (!this.modal || !btn) return;

        GameAnalytics.addDesignEvent('modal:privacy:show');

        btn.addEventListener('click', () => {
            GameAnalytics.addDesignEvent('button:accept-privacy');
            const accepted = Date.now();
            this.privacyModal.style = 'display: none';
            setCookie('paintball-privacy', accepted.toString(), 356);
            this.lastAcceptedPrivacy = accepted;
        });
    }

    openUsernameModal() {
        this.modal.style = 'display: flex';
        const btn = document.getElementById('submit-button');
        const input = document.getElementById('username-input') as HTMLInputElement;

        if (!this.modal || !btn || !input) return;
        GameAnalytics.addDesignEvent('modal:username:show');

        btn.addEventListener('click', () => {
            GameAnalytics.addDesignEvent('button:enter-username');
            this.username = input.value;
            if (this.username === '') return;
            profile.setUsername(this.username);
            this.modal.style = 'display: none';
        });
    }
}
